/**
 * Lightweight HTML entity decoder for entity names returned by the AssetPro API.
 *
 * The backend returns names HTML-encoded (e.g. `Loader & Backhoe` arrives as
 * `Loader &amp; Backhoe`). Reclassification compares names by string equality,
 * so we must decode at the ingestion boundary or every lookup will miss.
 *
 * Covers the entities the backend actually emits — no need to pull in `he`.
 */

const NAMED = {
  amp: '&',
  lt: '<',
  gt: '>',
  quot: '"',
  apos: "'",
  nbsp: ' ',
};

export function decodeHtmlEntities(str) {
  if (typeof str !== 'string' || str.indexOf('&') === -1) return str;
  return str.replace(/&(#x?[0-9a-fA-F]+|[a-zA-Z]+);/g, (match, body) => {
    if (body[0] === '#') {
      const isHex = body[1] === 'x' || body[1] === 'X';
      const code = parseInt(body.slice(isHex ? 2 : 1), isHex ? 16 : 10);
      if (Number.isFinite(code)) return String.fromCodePoint(code);
      return match;
    }
    const lower = body.toLowerCase();
    return NAMED[lower] != null ? NAMED[lower] : match;
  });
}

/**
 * Decode the `name` (and optional `code`) field on every entity in an array
 * returned from the AssetPro API. Mutates a shallow copy — the originals are
 * not touched, but other fields are passed through by reference.
 */
export function decodeEntityNames(arr) {
  if (!Array.isArray(arr)) return arr;
  return arr.map(e => {
    if (!e || typeof e !== 'object') return e;
    const out = { ...e };
    if (typeof out.name === 'string') out.name = decodeHtmlEntities(out.name);
    if (typeof out.code === 'string') out.code = decodeHtmlEntities(out.code);
    return out;
  });
}
